import {
  GetUserSettingsBillingStorageLimitLockingOptions,
  GetUserSettingsBillingStorageLimitLockingResult,
  getUserSettingsBillingStorageLimitLocking,
  GetUserSettingsBillingStorageLimitLockingFields,
} from '../fetchers/user/settings/billing/storage_limit_locking';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import { assignMswData, useInternalState, serializeQuery } from '../hooks/internalState';
import { useCallback } from 'react';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetUserSettingsBillingStorageLimitLockingOptions<
  Fields extends GetUserSettingsBillingStorageLimitLockingFields,
> = GetUserSettingsBillingStorageLimitLockingOptions<Fields>;

/**
 * Get storage-limit locking metadata
 * This method returns storage-limit locking metadata for the specified user.
 */

export function useGetUserSettingsBillingStorageLimitLocking<
  Fields extends GetUserSettingsBillingStorageLimitLockingFields,
>(
  getOptions:
    | UseGetUserSettingsBillingStorageLimitLockingOptions<Fields>
    | (() => UseGetUserSettingsBillingStorageLimitLockingOptions<Fields> | null),
  config?: SWRConfiguration<GetUserSettingsBillingStorageLimitLockingResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options
      ? `/users/${options.where.userId}/settings/billing/storage_limit_locking${serializeQuery(
          options,
        )}`
      : () => null,
    options
      ? () => {
          return getUserSettingsBillingStorageLimitLocking({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetUserSettingsBillingStorageLimitLocking, {
    endpoint: '/users/:userId/settings/billing/storage_limit_locking',
    method: 'GET',
  });
}

/**
 * Get storage-limit locking metadata
 * This method returns storage-limit locking metadata for the specified user.
 */

export function useGetUserSettingsBillingStorageLimitLockingLazy<
  Fields extends GetUserSettingsBillingStorageLimitLockingFields,
>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<
    GetUserSettingsBillingStorageLimitLockingResult<Fields> | undefined
  >();

  let callback = useCallback(
    async (options: UseGetUserSettingsBillingStorageLimitLockingOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/users/${options.where.userId}/settings/billing/storage_limit_locking${serializeQuery(
            options,
          )}`,
          getUserSettingsBillingStorageLimitLocking({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetUserSettingsBillingStorageLimitLockingLazy, {
    endpoint: '/users/:userId/settings/billing/storage_limit_locking',
    method: 'GET',
  });
}
